import Icon from '@/components/ui/AppIcon';

interface TimelinePhase {
  phase: string;
  duration: string;
  activities: string[];
  icon: string;
}

interface ImplementationTimelineProps {
  phases: TimelinePhase[];
}

export default function ImplementationTimeline({ phases }: ImplementationTimelineProps) {
  return (
    <div className="bg-card rounded-xl shadow-card border border-border overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-primary to-brand-secondary p-6">
        <div className="flex items-center space-x-3">
          <Icon name="ClockIcon" size={32} className="text-white" />
          <div>
            <h3 className="text-xl font-headline font-bold text-white mb-1">
              Implementation Timeline
            </h3>
            <p className="text-sm font-body text-white/80">
              Typical project phases and duration
            </p>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="p-6">
        <div className="relative">
          {/* Vertical Line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-brand-primary via-brand-secondary to-brand-conversion" />

          {/* Phases */}
          <div className="space-y-8">
            {phases.map((phase, index) => (
              <div key={index} className="relative pl-16">
                {/* Icon Circle */}
                <div className="absolute left-0 w-12 h-12 bg-white border-4 border-brand-primary rounded-full flex items-center justify-center shadow-card">
                  <Icon name={phase.icon as any} size={20} className="text-brand-primary" />
                </div>

                {/* Content */}
                <div className="bg-muted rounded-lg p-4 border border-border">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-base font-headline font-semibold text-primary">
                      {phase.phase}
                    </h4>
                    <span className="px-3 py-1 bg-brand-primary/10 text-brand-primary rounded-full text-xs font-cta font-semibold">
                      {phase.duration}
                    </span>
                  </div>

                  <ul className="space-y-1.5">
                    {phase.activities.map((activity, actIndex) => (
                      <li key={actIndex} className="flex items-start space-x-2">
                        <Icon name="CheckIcon" size={14} className="text-brand-conversion mt-0.5 flex-shrink-0" />
                        <span className="text-xs font-body text-foreground">{activity}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-6 p-4 bg-brand-primary/5 rounded-lg border border-brand-primary/20">
          <div className="flex items-start space-x-2">
            <Icon name="InformationCircleIcon" size={20} className="text-brand-primary mt-0.5 flex-shrink-0" />
            <p className="text-xs font-body text-foreground">
              Timeline may vary based on project scope, complexity, and specific business requirements. We provide detailed project plans during consultation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}